import React, { useState, useEffect, useCallback } from "react";
import { SvgIcon } from "../SvgIcon";
import { apiGet, apiPut } from "../../api";
import { t, Locale } from "../../i18n";
import { IdentitiesAndAliases } from "./IdentitiesAndAliases";

interface ProfileData {
  fullLegalName: string;
  dateOfBirth: string;
  streetAddress: string;
  city: string;
  postalCode: string;
  country: string;
}

interface Errors {
  [key: string]: string;
}

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin",
  "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso",
  "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile",
  "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador", "Egypt", "El Salvador",
  "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia",
  "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "North Korea", "South Korea",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
  "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger",
  "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

function validateFullLegalName(name: string, locale: Locale): string {
  if (!name) return ""; // optional
  if (name.length > 200) {
    return t(locale, "profile.personalInfo.fullName.error.tooLong");
  }
  return "";
}

function validateDateOfBirth(date: string, locale: Locale): string {
  if (!date) return ""; // optional
  const [y, m, d] = date.split("-");

  if (!y || !m || !d) {
    return t(locale, "profile.personalInfo.dateOfBirth.error.incomplete");
  }

  const year = parseInt(y, 10);
  const month = parseInt(m, 10);
  const day = parseInt(d, 10);
  const currentYear = new Date().getFullYear();

  if (year < 1900 || year > currentYear) {
    return t(locale, "profile.personalInfo.dateOfBirth.error.yearRange");
  }
  if (month < 1 || month > 12) {
    return t(locale, "profile.personalInfo.dateOfBirth.error.monthRange");
  }

  // Validate day for the given month/year
  const selectedDate = new Date(`${y}-${m}-${d}`);
  if (selectedDate.getFullYear() !== year || selectedDate.getMonth() + 1 !== month || selectedDate.getDate() !== day) {
    return t(locale, "profile.personalInfo.dateOfBirth.error.invalid");
  }

  const today = new Date();
  if (selectedDate > today) {
    return t(locale, "profile.personalInfo.dateOfBirth.error.future");
  }

  return "";
}

function validateStreetAddress(address: string, locale: Locale): string {
  if (!address) return ""; // optional
  if (address.length > 100) {
    return t(locale, "profile.personalInfo.streetAddress.error");
  }
  return "";
}

function validateCity(city: string, locale: Locale): string {
  if (!city) return ""; // optional
  if (city.length > 100) {
    return t(locale, "profile.personalInfo.city.error");
  }
  return "";
}

function validatePostalCode(code: string, locale: Locale): string {
  if (!code) return ""; // optional
  if (code.length > 20) {
    return t(locale, "profile.personalInfo.postalCode.error");
  }
  return "";
}

export function ProfileSettings(props: { locale: Locale }) {
  const locale = props.locale;
  const [profile, setProfile] = useState<ProfileData>({
    fullLegalName: "",
    dateOfBirth: "",
    streetAddress: "",
    city: "",
    postalCode: "",
    country: ""
  });

  const [errors, setErrors] = useState<Errors>({});
  const [savedCard, setSavedCard] = useState<string | null>(null);
  const [loadingPersonalInfo, setLoadingPersonalInfo] = useState(true);
  const [personalInfoError, setPersonalInfoError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchPersonalInfo = useCallback(async () => {
    setLoadingPersonalInfo(true);
    setPersonalInfoError(null);
    try {
      const res = await apiGet<{
        full_name: string | null;
        date_of_birth: string | null;
        street_address: string | null;
        city: string | null;
        postal_code: string | null;
        country: string | null;
        phone_e164: string | null;
        recovery_email: string | null;
      }>("/v1/profile/personal-info");

      if (res.ok) {
        setProfile({
          fullLegalName: res.data.full_name || "",
          dateOfBirth: res.data.date_of_birth || "",
          streetAddress: res.data.street_address || "",
          city: res.data.city || "",
          postalCode: res.data.postal_code || "",
          country: res.data.country || ""
        });
      } else {
        setPersonalInfoError(res.error || t(locale, "profile.error.loadFailed"));
      }
    } catch (err) {
      setPersonalInfoError(t(locale, "profile.error.loadFailed"));
    } finally {
      setLoadingPersonalInfo(false);
    }
  }, [locale]);

  useEffect(() => {
    fetchPersonalInfo();
  }, [fetchPersonalInfo]);

  const handleDisplayChange = (field: keyof ProfileData, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePersonal = (): boolean => {
    const newErrors: Errors = {};
    newErrors.fullLegalName = validateFullLegalName(profile.fullLegalName, locale);
    newErrors.dateOfBirth = validateDateOfBirth(profile.dateOfBirth, locale);
    newErrors.streetAddress = validateStreetAddress(profile.streetAddress, locale);
    newErrors.city = validateCity(profile.city, locale);
    newErrors.postalCode = validatePostalCode(profile.postalCode, locale);

    const hasErrors = Object.values(newErrors).some(err => err);
    if (hasErrors) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSavePersonal = async () => {
    if (!validatePersonal()) return;

    setSaving(true);
    setPersonalInfoError(null);
    try {
      // Only send fields that have values
      const payload: Record<string, string> = {};
      if (profile.fullLegalName) payload.full_name = profile.fullLegalName;
      if (profile.dateOfBirth) payload.date_of_birth = profile.dateOfBirth;
      if (profile.streetAddress) payload.street_address = profile.streetAddress;
      if (profile.city) payload.city = profile.city;
      if (profile.postalCode) payload.postal_code = profile.postalCode;
      if (profile.country) payload.country = profile.country;

      const res = await apiPut<{ ok: boolean }>("/v1/profile/personal-info", payload);

      if (res.ok) {
        setSavedCard("personal");
        setTimeout(() => setSavedCard(null), 3000);
      } else {
        setPersonalInfoError(res.error || t(locale, "profile.error.saveFailed"));
      }
    } catch (err) {
      setPersonalInfoError(t(locale, "profile.error.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: "4px" }}>{t(locale, "profile.title")}</h2>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px" }}>
        {t(locale, "profile.description")}
      </p>

      {/* CARD 1: Identities & Aliases */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <IdentitiesAndAliases locale={locale} />
      </div>

      {/* CARD 2: Personal Information */}
      <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px", marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "profile.personalInfo.title")}</h3>

        {loadingPersonalInfo && (
          <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", padding: "12px", background: "var(--surface)", borderRadius: "6px" }}>
            {t(locale, "common.loading")}
          </div>
        )}

        {personalInfoError && !loadingPersonalInfo && (
          <div style={{ fontSize: "12px", color: "#ef4444", marginBottom: "16px", padding: "12px", background: "rgba(239, 68, 68, 0.1)", borderRadius: "6px", borderLeft: "3px solid #ef4444" }}>
            {personalInfoError}
          </div>
        )}

        <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.6", padding: "12px", background: "rgba(96, 165, 250, 0.1)", borderRadius: "6px", borderLeft: "3px solid var(--accent)" }}>
          {t(locale, "profile.personalInfo.privacyNote")}
        </div>

        {/* Full Legal Name */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "8px", color: "var(--text)" }}>
            {t(locale, "profile.personalInfo.fullName")}
          </label>
          <input
            type="text"
            maxLength={200}
            value={profile.fullLegalName}
            onChange={(e) => handleDisplayChange("fullLegalName", e.target.value)}
            placeholder={t(locale, "profile.personalInfo.fullName.placeholder")}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${errors.fullLegalName ? "#ef4444" : "var(--control-border)"}`,
              borderRadius: "6px",
              fontSize: "13px",
              fontFamily: "inherit",
              color: "var(--text)",
              background: "var(--surface)",
              boxSizing: "border-box"
            }}
            onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
            onBlur={(e) => (e.target.style.borderColor = errors.fullLegalName ? "#ef4444" : "var(--control-border)")}
          />
          {errors.fullLegalName && (
            <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>
              {errors.fullLegalName}
            </div>
          )}
        </div>

        {/* Date of Birth */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "8px", color: "var(--text)" }}>
            {t(locale, "profile.personalInfo.dateOfBirth")}
          </label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              placeholder="YYYY"
              value={profile.dateOfBirth ? profile.dateOfBirth.split("-")[0] : ""}
              onChange={(e) => {
                const [y, m, d] = profile.dateOfBirth.split("-");
                handleDisplayChange("dateOfBirth", `${e.target.value}-${m || ""}${!m || !e.target.value ? "-" : ""}-${d || ""}`);
              }}
              style={{
                flex: 1,
                maxWidth: "100px",
                padding: "10px 12px",
                border: `1px solid ${errors.dateOfBirth ? "#ef4444" : "var(--control-border)"}`,
                borderRadius: "6px",
                fontSize: "13px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box",
                textAlign: "center"
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
              onBlur={(e) => (e.target.style.borderColor = errors.dateOfBirth ? "#ef4444" : "var(--control-border)")}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              placeholder="MM"
              value={profile.dateOfBirth ? profile.dateOfBirth.split("-")[1] : ""}
              onChange={(e) => {
                const [y, m, d] = profile.dateOfBirth.split("-");
                handleDisplayChange("dateOfBirth", `${y || ""}-${e.target.value}-${d || ""}`);
              }}
              style={{
                flex: 1,
                maxWidth: "70px",
                padding: "10px 12px",
                border: `1px solid ${errors.dateOfBirth ? "#ef4444" : "var(--control-border)"}`,
                borderRadius: "6px",
                fontSize: "13px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box",
                textAlign: "center"
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
              onBlur={(e) => (e.target.style.borderColor = errors.dateOfBirth ? "#ef4444" : "var(--control-border)")}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              placeholder="DD"
              value={profile.dateOfBirth ? profile.dateOfBirth.split("-")[2] : ""}
              onChange={(e) => {
                const [y, m, d] = profile.dateOfBirth.split("-");
                handleDisplayChange("dateOfBirth", `${y || ""}-${m || ""}-${e.target.value}`);
              }}
              style={{
                flex: 1,
                maxWidth: "70px",
                padding: "10px 12px",
                border: `1px solid ${errors.dateOfBirth ? "#ef4444" : "var(--control-border)"}`,
                borderRadius: "6px",
                fontSize: "13px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box",
                textAlign: "center"
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
              onBlur={(e) => (e.target.style.borderColor = errors.dateOfBirth ? "#ef4444" : "var(--control-border)")}
            />
          </div>
          {errors.dateOfBirth && (
            <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>
              {errors.dateOfBirth}
            </div>
          )}
        </div>

        {/* Address Section */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>
              {t(locale, "profile.personalInfo.streetAddress")}
            </label>
            <input
              type="text"
              value={profile.streetAddress}
              onChange={(e) => handleDisplayChange("streetAddress", e.target.value)}
              placeholder="123 Main St"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${errors.streetAddress ? "#ef4444" : "var(--control-border)"}`,
                borderRadius: "6px",
                fontSize: "13px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box"
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
              onBlur={(e) => (e.target.style.borderColor = errors.streetAddress ? "#ef4444" : "var(--control-border)")}
            />
            {errors.streetAddress && (
              <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>
                {errors.streetAddress}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>
                {t(locale, "profile.personalInfo.city")}
              </label>
              <input
                type="text"
                value={profile.city}
                onChange={(e) => handleDisplayChange("city", e.target.value)}
                placeholder="Stockholm"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${errors.city ? "#ef4444" : "var(--control-border)"}`,
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  color: "var(--text)",
                  background: "var(--surface)",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
                onBlur={(e) => (e.target.style.borderColor = errors.city ? "#ef4444" : "var(--control-border)")}
              />
              {errors.city && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>
                  {errors.city}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>
                {t(locale, "profile.personalInfo.postalCode")}
              </label>
              <input
                type="text"
                value={profile.postalCode}
                onChange={(e) => handleDisplayChange("postalCode", e.target.value)}
                placeholder="12345"
                maxLength={20}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${errors.postalCode ? "#ef4444" : "var(--control-border)"}`,
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  color: "var(--text)",
                  background: "var(--surface)",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
                onBlur={(e) => (e.target.style.borderColor = errors.postalCode ? "#ef4444" : "var(--control-border)")}
              />
              {errors.postalCode && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>
                  {errors.postalCode}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--text)" }}>
              {t(locale, "profile.personalInfo.country")}
            </label>
            <select
              value={profile.country}
              onChange={(e) => handleDisplayChange("country", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid var(--control-border)",
                borderRadius: "6px",
                fontSize: "13px",
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--surface)",
                boxSizing: "border-box",
                cursor: "pointer"
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "var(--control-border)")}
            >
              <option value="">{t(locale, "profile.personalInfo.country.placeholder")}</option>
              {COUNTRIES.map(country => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div style={{ fontSize: "12px", color: "var(--muted)" }}>
            {t(locale, "profile.personalInfo.addressNote")}
          </div>
        </div>

        <button
          onClick={handleSavePersonal}
          disabled={loadingPersonalInfo || saving}
          style={{
            background: loadingPersonalInfo || saving ? "var(--text-secondary)" : "var(--accent)",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: loadingPersonalInfo || saving ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: 600,
            opacity: loadingPersonalInfo || saving ? 0.6 : 1
          }}
          onMouseEnter={(e) => !loadingPersonalInfo && !saving && (e.currentTarget.style.opacity = "0.9")}
          onMouseLeave={(e) => !loadingPersonalInfo && !saving && (e.currentTarget.style.opacity = "1")}
        >
          {saving ? t(locale, "profile.saving") : t(locale, "profile.save")}
        </button>

        {savedCard === "personal" && (
          <div style={{ fontSize: "13px", color: "#10b981", fontWeight: 500, marginTop: "12px" }}>
            {t(locale, "profile.saved")}
          </div>
        )}

        {saving && personalInfoError && (
          <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "12px" }}>
            {personalInfoError}
          </div>
        )}
      </div>
    </div>
  );
}
